import { createAsyncThunk } from '@reduxjs/toolkit';
import { realTimeApiInstance } from '../services/api';
import { RootState } from '../store';

interface FetchParams {
  assetId: string;
  startDate: string;
  endDate: string;
  popDate: string;
}

const inFlightRequests = new Map<string, Promise<any>>();
const generateRequestKey = (params: FetchParams) =>
  `${params.assetId}|${params.startDate}|${params.endDate}|${params.popDate}`;

export const fetchAssetDowntimeAndShutdownData = createAsyncThunk(
  'asset/fetchDowntime',
  async (params: FetchParams, { signal, rejectWithValue }) => {
    const requestKey = generateRequestKey(params);
    if (inFlightRequests.has(requestKey)) {
      return inFlightRequests.get(requestKey);
    }
    const requestPromise = (async () => {
      try {
        const controller = new AbortController();

        // Forward abort signal
        signal.addEventListener('abort', () => {
          controller.abort();
        });

        const apiEndpoint = `/RealTimeData/GetAssetDowntimeAndShutdownData?AssetId=${params.assetId}&StartDate=${params.startDate}&EndDate=${params.endDate}&POPDate=${params.popDate}`;
        const response = await realTimeApiInstance.get(apiEndpoint, { signal: controller.signal });
        return response.data || [];
      } catch (error: any) {
        console.error('Error fetching asset downtime and shutdown data:', error);
        return rejectWithValue(error.message || 'Failed to fetch asset downtime data');
      } finally {
        inFlightRequests.delete(requestKey);
      }
    })();

    inFlightRequests.set(requestKey, requestPromise);
    return requestPromise;
  },
  {
    condition: (params, { getState }) => {
      const state = getState() as RootState;
      const { loading, lastFetchedParams } = state.shutdownAndDropdownSlice;

      // Don't fetch if already loading
      if (loading) {
        return false;
      }

      // Only fetch if params have changed
      const currentParamsKey = generateRequestKey(params);
      if (lastFetchedParams && lastFetchedParams === currentParamsKey) {
        return false;
      }

      return true;
    },
  },
);
